import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  FormGroup,
  Button,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  Timer as TimerIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'

import { RootState, AppDispatch } from '../app/store'
import { fetchAssessmentById, submitAssessment, clearCurrentAssessment } from '../features/assessments/assessmentSlice'
import { Question, AssessmentSubmission } from '../types'

interface UserAnswer {
  questionId: number
  answer: string | string[]
}

interface AssessmentTakingProps {
  assessmentId: string
  onComplete?: (result: AssessmentSubmission) => void
  onCancel?: () => void
  embedded?: boolean // Whether this is embedded in a course view
}

const AssessmentTaking: React.FC<AssessmentTakingProps> = ({ 
  assessmentId, 
  onComplete, 
  onCancel,
  embedded = false 
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const { currentAssessment, isLoading, error } = useSelector(
    (state: RootState) => state.assessments
  )

  const [answers, setAnswers] = useState<UserAnswer[]>([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isTimedOut, setIsTimedOut] = useState(false)
  const [questionsToShow, setQuestionsToShow] = useState<Question[]>([])
  const [submissionResult, setSubmissionResult] = useState<AssessmentSubmission | null>(null)
  const [showResults, setShowResults] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Reset all state when assessmentId changes
  useEffect(() => {
    // Clear Redux state first
    dispatch(clearCurrentAssessment())
    // Clear all local state
    setAnswers([])
    setCurrentQuestionIndex(0)
    setTimeRemaining(null)
    setIsSubmitting(false)
    setIsTimedOut(false)
    setQuestionsToShow([])
    setSubmissionResult(null)
    setShowResults(false)
  }, [assessmentId, dispatch])

  // Fetch assessment when assessmentId changes
  useEffect(() => {
    if (assessmentId) {
      dispatch(fetchAssessmentById(assessmentId))
    }
  }, [dispatch, assessmentId])

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearCurrentAssessment())
    }
  }, [dispatch])

  // Handle random question selection
  useEffect(() => {
    const fullBank = currentAssessment?.all_questions
    const providedQuestions = currentAssessment?.questions

    const availableQuestions = (fullBank && fullBank.length > 0)
      ? fullBank
      : (providedQuestions || [])

    if (availableQuestions.length === 0) {
      setQuestionsToShow([])
      return
    }

    const questionsToShowCount = currentAssessment?.questions_to_show || availableQuestions.length

    // Backend already provides randomized questions, so don't sort by order
    // Just use them as-is to maintain the randomization
    if (questionsToShowCount >= availableQuestions.length) {
      setQuestionsToShow([...availableQuestions])
    } else {
      // If we need fewer questions, the backend already selected random ones
      const selected = availableQuestions.slice(0, questionsToShowCount)
      setQuestionsToShow(selected)
    }
  }, [currentAssessment])

  // Timer logic
  useEffect(() => {
    if (currentAssessment?.time_limit && timeRemaining === null) {
      setTimeRemaining(currentAssessment.time_limit * 60)
    }
  }, [currentAssessment, timeRemaining])

  useEffect(() => {
    if (timeRemaining !== null && timeRemaining > 0 && !showResults && !isTimedOut) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev === null || prev <= 1) {
            setIsTimedOut(true)
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(timer)
    }
  }, [timeRemaining, showResults, isTimedOut])

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  const handleAnswerChange = (questionId: number, answer: string | string[]) => {
    setAnswers((prev) => {
      const existing = prev.find((a) => a.questionId === questionId)
      if (existing) {
        return prev.map((a) =>
          a.questionId === questionId ? { ...a, answer } : a
        )
      }
      return [...prev, { questionId, answer }]
    })
  }

  const getCurrentQuestion = () => {
    return questionsToShow[currentQuestionIndex]
  }

  const handleRestart = () => {
    // Reset local state
    setAnswers([])
    setCurrentQuestionIndex(0)
    setIsSubmitting(false)
    setIsTimedOut(false)
    setSubmissionResult(null)
    setShowResults(false)
    
    // Re-fetch assessment to get a fresh start (and potentially new random questions)
    if (assessmentId) {
      dispatch(fetchAssessmentById(assessmentId))
    }
  }

  const handleSubmitAssessment = async () => {
    if (isSubmitting) return

    setIsSubmitting(true)

    try {
      // Format answers as a list of objects for the backend API
      const formattedAnswers = answers.map(({ questionId, answer }) => {
        const selectedChoices = Array.isArray(answer) 
          ? answer.map(id => parseInt(id)) 
          : [parseInt(answer as string)]
        
        return {
          question: questionId,
          answer_text: Array.isArray(answer) ? answer.join(',') : answer,
          selected_choices: selectedChoices
        }
      })

      const resultAction = await dispatch(
        submitAssessment({
          assessmentId: assessmentId,
          answers: formattedAnswers,
        })
      )

      if (submitAssessment.fulfilled.match(resultAction)) {
        const result = resultAction.payload
        setSubmissionResult(result)
        setShowResults(true)
        
        // Don't refresh enrollments here - let parent handle it when user clicks Continue button
        // This prevents parent component from re-rendering and hiding the results
      } else {
        console.error('❌ Assessment submission failed:', resultAction.error)
      }
    } catch (err) {
      console.error('❌ Error submitting assessment:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questionsToShow.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    }
  }

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
    }
  }

  const handleCancelConfirm = () => {
    setShowCancelDialog(false)
    if (onCancel) {
      onCancel()
    }
  }

  const renderQuestionInput = (question: Question) => {
    const currentAnswer = answers.find((a) => a.questionId === question.id)?.answer

    switch (question.type) {
      case 'multiple_choice':
      case 'true_false':
        return (
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
              {question.text}
            </FormLabel>
            <RadioGroup
              value={(currentAnswer as string) || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            >
              {question.choices?.map((choice) => (
                <FormControlLabel
                  key={choice.id}
                  value={choice.id.toString()}
                  control={<Radio />}
                  label={choice.text}
                  sx={{ mb: 1 }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )

      case 'multi_select':
        const selectedChoices = (currentAnswer as string[]) || []
        return (
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
              {question.text}
              <Typography variant="caption" display="block" sx={{ mt: 0.5, fontWeight: 400 }}>
                (Select all that apply)
              </Typography>
            </FormLabel>
            <FormGroup>
              {question.choices?.map((choice) => (
                <FormControlLabel
                  key={choice.id}
                  control={
                    <Checkbox
                      checked={selectedChoices.includes(choice.id.toString())}
                      onChange={(e) => {
                        const choiceId = choice.id.toString()
                        const newSelection = e.target.checked
                          ? [...selectedChoices, choiceId]
                          : selectedChoices.filter((id) => id !== choiceId)
                        handleAnswerChange(question.id, newSelection)
                      }}
                    />
                  }
                  label={choice.text}
                  sx={{ mb: 1 }}
                />
              ))}
            </FormGroup>
          </FormControl>
        )

      default:
        return <Typography>Unsupported question type</Typography>
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
          Loading Assessment...
        </Typography>
      </Box>
    )
  }

  if (isTimedOut) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: embedded ? 0 : 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <TimerIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="error">
              Time's Up!
            </Typography>
            <Typography variant="body1" paragraph>
              The time limit for this assessment has expired.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 4 }}>
              You need to start from the beginning to complete this assessment.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<RefreshIcon />}
              onClick={handleRestart}
            >
              Start Over
            </Button>
          </CardContent>
        </Card>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading assessment: {error}
      </Alert>
    )
  }

  if (!currentAssessment) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Assessment not found
      </Alert>
    )
  }

  const currentQuestion = getCurrentQuestion()
  if (!currentQuestion) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        No questions available for this assessment
      </Alert>
    )
  }

  // Results view
  if (showResults && submissionResult) {
    const isPassed = submissionResult.is_passed || false
    const score = Number(submissionResult.score) || 0
    const pointsEarned = Number(submissionResult.points_earned) || 0
    const totalPoints = Number(submissionResult.total_points) || 0

    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: embedded ? 0 : 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              {isPassed ? (
                <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              ) : (
                <CancelIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              )}
              
              <Typography variant="h4" gutterBottom>
                {isPassed ? 'Congratulations!' : 'Assessment Not Passed'}
              </Typography>
              
              <Typography variant="body1" color="text.secondary" paragraph>
                {submissionResult.result_message || (isPassed 
                  ? 'You have successfully passed the assessment.'
                  : 'You did not meet the minimum passing score.'
                )}
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, my: 4 }}>
                <Box>
                  <Typography variant="h3" color={isPassed ? 'success.main' : 'error.main'}>
                    {score.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your Score
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="h3">
                    {pointsEarned}/{totalPoints}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Points Earned
                  </Typography>
                </Box>
              </Box>

              <Chip
                label={`Attempt #${submissionResult.attempt_number}`}
                color="primary"
                sx={{ mb: 2 }}
              />

              {/* Continue button to return to course view */}
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => {
                    // Call onComplete to notify parent and refresh data
                    if (onComplete) {
                      onComplete(submissionResult)
                    }
                    // Hide results view
                    setShowResults(false)
                  }}
                >
                  Continue
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    )
  }

  // Assessment taking view
  const progress = ((currentQuestionIndex + 1) / questionsToShow.length) * 100
  const answeredCount = answers.length

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: embedded ? 0 : 3 }}>
      {/* Header with timer and progress */}
      {embedded ? (
        // Simpler design for embedded assessments
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
              <AssignmentIcon fontSize="small" />
              {currentAssessment.title}
            </Typography>
            {timeRemaining !== null && (
              <Chip
                icon={<TimerIcon />}
                label={formatTime(timeRemaining)}
                color={timeRemaining < 300 ? 'error' : 'default'}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
          
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Question {currentQuestionIndex + 1} of {questionsToShow.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {answeredCount} of {questionsToShow.length} answered
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
          </Box>
        </Box>
      ) : (
        // Full card design for standalone assessments
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIcon />
                {currentAssessment.title}
              </Typography>
              {timeRemaining !== null && (
                <Chip
                  icon={<TimerIcon />}
                  label={formatTime(timeRemaining)}
                  color={timeRemaining < 300 ? 'error' : 'primary'}
                  sx={{ fontWeight: 'bold' }}
                />
              )}
            </Box>
            
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Question {currentQuestionIndex + 1} of {questionsToShow.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {answeredCount} of {questionsToShow.length} answered
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Question */}
      <Paper sx={{ p: embedded ? 2 : 3, mb: 3 }}>
        <Chip 
          label={`${currentQuestion.points} ${currentQuestion.points === 1 ? 'point' : 'points'}`} 
          size="small" 
          color={embedded ? 'default' : 'primary'}
          variant={embedded ? 'outlined' : 'filled'}
          sx={{ mb: 2 }}
        />
        {renderQuestionInput(currentQuestion)}
      </Paper>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        {/* Previous button on the left */}
        <Button
          variant="outlined"
          onClick={handlePreviousQuestion}
          disabled={currentQuestionIndex === 0}
          size={embedded ? 'medium' : 'large'}
        >
          Previous
        </Button>

        {/* Next/Submit and Cancel buttons on the right */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {embedded && onCancel && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setShowCancelDialog(true)}
              size="medium"
            >
              Cancel
            </Button>
          )}
          
          {/* Show Next button if not on last question, otherwise show Submit */}
          {currentQuestionIndex < questionsToShow.length - 1 ? (
            <Button
              variant={embedded ? 'outlined' : 'contained'}
              color={embedded ? 'primary' : 'primary'}
              onClick={handleNextQuestion}
              size={embedded ? 'medium' : 'large'}
            >
              Next
            </Button>
          ) : (
            <Button
              variant={embedded ? 'outlined' : 'contained'}
              color="success"
              onClick={handleSubmitAssessment}
              disabled={isSubmitting || answeredCount === 0}
              size={embedded ? 'medium' : 'large'}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Assessment'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)}>
        <DialogTitle>Cancel Assessment?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this assessment? Your progress will not be saved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>
            Continue Assessment
          </Button>
          <Button onClick={handleCancelConfirm} color="error" variant="contained">
            Cancel Assessment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AssessmentTaking
